import { Camera, History, MessageCircle, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { PrivatePhoto } from "@/components/PrivatePhoto";
import { Button, ErrorBanner, Input, Modal, Spinner, StatusBadge } from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useFormat, useT, type Chave } from "@/lib/i18n";
import {
  useAppointmentAction,
  useAppointmentEvents,
  useDeletePhoto,
  useUpdateAppointment,
  useUploadPhoto,
} from "@/lib/queries";
import type { Appointment, Me, PhotoKind } from "@/lib/types";

/** Ações possíveis a partir de cada estado — espelha TRANSITIONS do backend.
 *  A mesma tabela da Agenda: o rótulo é CHAVE, traduzida no JSX. */
const ACTIONS: Record<string, { action: string; label: Chave; variant?: "danger" }[]> = {
  pending: [
    { action: "confirm", label: "action.confirm" },
    { action: "cancel", label: "action.cancel", variant: "danger" },
  ],
  confirmed: [
    { action: "start", label: "action.start" },
    { action: "no-show", label: "action.noShow", variant: "danger" },
    { action: "cancel", label: "action.cancel", variant: "danger" },
  ],
  in_progress: [{ action: "complete", label: "action.complete" }],
};

/** Limite de 5 MB — o mesmo de `MAX_UPLOAD_BYTES` em apps/common/images.py
 *  e do ImageField da galeria. Recusar aqui evita subir 8 MB pelo 4G do
 *  pátio para o Django devolver 413 no fim. */
const MAX_BYTES = 5 * 1024 * 1024;
const ACEITOS = ["image/jpeg", "image/png", "image/webp"];

const MOMENTOS: { kind: PhotoKind; label: Chave }[] = [
  { kind: "arrival", label: "photos.arrival" },
  { kind: "departure", label: "photos.departure" },
];

/** Ações do log que a tela sabe nomear. O que não estiver aqui aparece
 *  cru — um evento novo no backend é melhor visível e feio do que
 *  invisível e bonito. */
const EVENTOS: Record<string, Chave> = {
  created: "event.created",
  confirmed: "event.confirmed",
  started: "event.started",
  completed: "event.completed",
  canceled: "event.canceled",
  no_show: "event.noShow",
  rescheduled: "event.rescheduled",
  quoted: "event.quoted",
  photo_added: "event.photoAdded",
  photo_removed: "event.photoRemoved",
};

/** A ficha do atendimento: o que foi combinado, o que se viu no carro e
 *  como ele chegou.
 *
 *  Nasceu do orçamento — a visita cujo produto É a descrição do veículo e
 *  um número —, mas a ficha é a mesma para todo atendimento: a troca de
 *  óleo em que o mecânico reparou na pastilha gasta gera exatamente o
 *  mesmo texto. O que muda no orçamento é só o destaque: o campo do valor
 *  orçado sobe para o topo, porque é ele que o cliente está esperando.
 */
export default function AppointmentDetailModal({
  appointment: a,
  me,
  onClose,
}: {
  appointment: Appointment | null;
  me: Me;
  onClose: () => void;
}) {
  const t = useT();
  const { money, hhmm, fullDate, phone, decimalInput } = useFormat();
  const update = useUpdateAppointment();
  const act = useAppointmentAction();

  const [laudo, setLaudo] = useState("");
  const [interna, setInterna] = useState("");
  const [orcado, setOrcado] = useState("");
  const [historico, setHistorico] = useState(false);

  // Recarrega os campos a cada atendimento aberto. Sem a dependência no
  // id, abrir o próximo cartão mostraria o laudo do anterior — e alguém
  // salvaria o texto do carro errado por cima do certo.
  useEffect(() => {
    if (!a) return;
    setLaudo(a.vehicle_report);
    setInterna(a.internal_notes);
    setOrcado(a.quoted_price === null ? "" : decimalInput(a.quoted_price));
    setHistorico(false);
    // `decimalInput` vem de um hook e muda de identidade a cada render;
    // incluí-lo aqui reabriria o efeito e apagaria o que está sendo
    // digitado. O que importa é o atendimento ter mudado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id]);

  if (!a) return null;

  const pais = me.garage.region.country;
  const whatsapp = `https://wa.me/${a.customer_detail.phone.replace(/\D/g, "")}`;
  const actions = ACTIONS[a.status] ?? [];
  const erro = update.isError ? toApiError(update.error) : null;

  const sujo =
    laudo !== a.vehicle_report ||
    interna !== a.internal_notes ||
    orcado !== (a.quoted_price === null ? "" : decimalInput(a.quoted_price));

  const salvar = () =>
    update.mutate({
      id: a.id,
      vehicle_report: laudo,
      internal_notes: interna,
      // Campo vazio é `null`, não zero: "ninguém orçou ainda" e "olhei e
      // não vou cobrar" são respostas diferentes, e o relatório precisa
      // distinguir uma da outra.
      quoted_price: orcado.trim() === "" ? null : orcado.replace(",", "."),
    });

  return (
    <Modal open onClose={onClose} title={t("detail.title")} wide>
      <div className="space-y-5">
        {/* cabeçalho: quando, quem, qual carro */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold tabular-nums">{hhmm(a.starts_at)}</span>
              <span className="text-sm text-steel-dim">–{hhmm(a.ends_at)}</span>
              <StatusBadge status={a.status} />
              {a.is_quote && (
                <span className="rounded-[2px] border border-sky-700/60 bg-sky-950/50 px-1.5 py-0.5 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-300">
                  {t("detail.quote")}
                </span>
              )}
            </div>
            {/* `first-letter` e não `capitalize`, como na Agenda: o
                segundo maiusculiza cada palavra e produz "18 De
                Setembro", que nenhum idioma escreve assim. */}
            <p className="mt-0.5 text-sm text-steel-muted first-letter:uppercase">
              {fullDate(a.starts_at)}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {a.vehicle_detail && (
                <span className="plate">{a.vehicle_detail.formatted_plate}</span>
              )}
              <span className="font-medium">{a.customer_detail.name}</span>
            </div>
            <p className="text-sm text-steel-muted">
              {a.items.map((i) => i.service_name).join(" + ")} · {a.professional_name}
              {a.vehicle_detail && ` · ${a.vehicle_detail.label}`}
            </p>
          </div>

          <div className="text-right">
            {me.permissions.see_financials && !a.is_quote && (
              <p className="font-semibold">{money(a.total_price)}</p>
            )}
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-400 hover:underline"
            >
              <MessageCircle size={14} />
              {phone(a.customer_detail.phone, pais)}
            </a>
          </div>
        </div>

        {/* o que o cliente pediu — leitura, veio do agendamento */}
        {a.customer_notes && (
          <div className="rounded-[4px] border border-line bg-carbon-900 p-3">
            <p className="eyebrow mb-1 text-steel-dim">{t("detail.customerNotes")}</p>
            <p className="whitespace-pre-wrap text-sm text-steel">{a.customer_notes}</p>
          </div>
        )}

        {/* valor orçado primeiro no orçamento: é o produto da visita */}
        {a.is_quote && me.permissions.see_financials && (
          <Input
            label={t("detail.quotedPrice")}
            hint={t("detail.quotedPriceHint")}
            type="text"
            inputMode="decimal"
            value={orcado}
            onChange={(e) => setOrcado(e.target.value)}
          />
        )}

        <Campo
          label={t("detail.report")}
          hint={a.is_quote ? t("detail.reportHintQuote") : t("detail.reportHint")}
          value={laudo}
          onChange={setLaudo}
          rows={a.is_quote ? 5 : 3}
        />

        <Campo
          label={t("detail.internalNotes")}
          hint={t("detail.internalNotesHint")}
          value={interna}
          onChange={setInterna}
          rows={2}
        />

        {!a.is_quote && me.permissions.see_financials && a.quoted_price !== null && (
          <p className="text-sm text-steel-muted">
            {t("detail.quotedPrice")}: <span className="text-steel">{money(a.quoted_price)}</span>
          </p>
        )}

        <ErrorBanner message={erro?.message} />

        <div className="flex items-center gap-2">
          <Button onClick={salvar} disabled={!sujo || update.isPending}>
            {update.isPending ? (
              <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
            ) : (
              t("common.save")
            )}
          </Button>
          {/* Sem toast no projeto: o próprio botão diz o que aconteceu. */}
          {!sujo && update.isSuccess && (
            <span className="text-sm text-racing-400">{t("detail.saved")}</span>
          )}
        </div>

        {/* vistoria */}
        <div className="border-t border-line pt-4">
          <p className="eyebrow mb-1 text-steel-dim">{t("photos.title")}</p>
          <p className="mb-3 text-xs text-steel-muted">{t("photos.hint")}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {MOMENTOS.map(({ kind, label }) => (
              <Vistoria
                key={kind}
                appointment={a}
                kind={kind}
                titulo={t(label)}
              />
            ))}
          </div>
        </div>

        {/* transições — as mesmas da lista do dia */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            {actions.map((acao) => (
              <Button
                key={acao.action}
                size="sm"
                variant={acao.variant ?? "secondary"}
                disabled={act.isPending}
                onClick={() => act.mutate({ id: a.id, action: acao.action })}
              >
                {t(acao.label)}
              </Button>
            ))}
          </div>
        )}

        {/* histórico: fechado por padrão, e só busca quando abre */}
        <div className="border-t border-line pt-4">
          <button
            type="button"
            onClick={() => setHistorico((v) => !v)}
            aria-expanded={historico}
            className="flex items-center gap-2 text-sm text-steel-muted transition hover:text-steel"
          >
            <History size={14} />
            {t("detail.history")}
          </button>
          {historico && <Historico id={a.id} />}
        </div>
      </div>
    </Modal>
  );
}

/** Textarea com o mesmo rosto do `Input` do projeto. Existe aqui e não em
 *  ui.tsx porque esta é a primeira tela do painel com texto longo; sobe
 *  para lá quando a segunda aparecer. */
function Campo({
  label,
  hint,
  value,
  onChange,
  rows,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <label className="block">
      <span className="eyebrow mb-1.5 block text-steel-dim">{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-[4px] border border-line bg-carbon-900 px-3 py-2 text-sm text-steel outline-none transition focus:border-nitro-500"
      />
      <span className="mt-1 block text-xs text-steel-dim">{hint}</span>
    </label>
  );
}

/** Entrada ou saída: a grade de fotos de um dos dois momentos. */
function Vistoria({
  appointment: a,
  kind,
  titulo,
}: {
  appointment: Appointment;
  kind: PhotoKind;
  titulo: string;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const upload = useUploadPhoto();
  const remove = useDeletePhoto();

  const fotos = a.photos.filter((p) => p.kind === kind);
  const cheio = fotos.length >= 8;

  const escolher = (file: File | null) => {
    setErro(null);
    if (!file) return;
    if (!ACEITOS.includes(file.type)) return setErro(t("photos.format"));
    if (file.size > MAX_BYTES)
      return setErro(t("image.tooBig", { size: (file.size / 1024 / 1024).toFixed(1) }));
    upload.mutate(
      { id: a.id, file, kind },
      { onError: (e) => setErro(toApiError(e).message) },
    );
    // Zerar o input permite reescolher o MESMO arquivo depois de um erro;
    // sem isto o `change` não dispara e a tela parece travada.
    if (input.current) input.current.value = "";
  };

  return (
    <div className="rounded-[4px] border border-line bg-carbon-900 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-steel">{titulo}</span>
        <span className="text-xs tabular-nums text-steel-dim">{fotos.length}/8</span>
      </div>

      <div className="mb-2 grid grid-cols-3 gap-1.5">
        {fotos.map((foto) => (
          <div key={foto.id} className="group relative aspect-square overflow-hidden rounded-[2px]">
            <PrivatePhoto
              appointmentId={a.id}
              photoId={foto.id}
              alt={foto.caption || titulo}
              className="size-full object-cover"
            />
            <button
              type="button"
              onClick={() => remove.mutate({ id: a.id, photoId: foto.id })}
              disabled={remove.isPending}
              aria-label={t("photos.remove")}
              className="absolute right-1 top-1 rounded-[2px] bg-black/70 p-1 text-steel-dim opacity-0 transition hover:text-red-400 focus-visible:opacity-100 group-hover:opacity-100"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>

      <input
        ref={input}
        type="file"
        accept={ACEITOS.join(",")}
        // `capture` abre a câmera direto no celular do mecânico, que é
        // onde a foto é tirada — no desktop o atributo é ignorado e o
        // seletor de arquivos aparece normalmente.
        capture="environment"
        className="hidden"
        onChange={(e) => escolher(e.target.files?.[0] ?? null)}
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={cheio || upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? <Spinner className="size-4" /> : <Camera size={14} />}
        {cheio ? t("photos.full") : t("photos.add")}
      </Button>
      {erro && <p className="mt-1.5 text-xs text-red-400">{erro}</p>}
    </div>
  );
}

function Historico({ id }: { id: number }) {
  const t = useT();
  const { hhmm, dayLabel } = useFormat();
  const { data, isLoading } = useAppointmentEvents(id, true);

  if (isLoading) return <Spinner className="mt-3 size-4" />;
  if (!data?.length) return <p className="mt-3 text-sm text-steel-dim">{t("detail.noHistory")}</p>;

  return (
    <ul className="mt-3 space-y-1.5">
      {data.map((evento) => (
        <li key={evento.id} className="flex flex-wrap gap-x-2 text-xs text-steel-muted">
          <span className="tabular-nums text-steel-dim">
            {dayLabel(evento.created_at)} {hhmm(evento.created_at)}
          </span>
          <span className="text-steel">
            {EVENTOS[evento.action] ? t(EVENTOS[evento.action]) : evento.action}
          </span>
          <span className="text-steel-dim">{evento.actor_name}</span>
        </li>
      ))}
    </ul>
  );
}
